/*
 * =====================================================================================================================
 * A/B TEST #68 – "Recently viewed" panel on collection pages (PLP)
 * =====================================================================================================================
 * Reads the product snapshots saved by snippets/ab-recently-viewed.liquid on product pages and renders them without
 * any network request. Add to cart reuses the theme: <product-form> for direct adds, <quick-buy-modal> when the
 * product has options to choose (or when a direct add fails, e.g. the saved snapshot is stale / sold out).
 *
 * Only loaded on collection templates. Remove this file (and the snippet/CSS) if the test loses.
 * =====================================================================================================================
 */
(() => {
  if (window.customElements.get('ab-recently-viewed')) return;

  const STORAGE_KEY = 'ab_rv_products_v1';
  const MAX_VISIBLE = 8;
  const MAX_THUMBS = 3;
  const DESKTOP_QUERY = window.matchMedia('(min-width: 1000px)');

  const readSavedProducts = () => {
    try {
      const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  };

  // Loaded only when there is something to show, so the stylesheet never blocks rendering of the collection page.
  const loadStylesheet = (href) => new Promise((resolve, reject) => {
    if (!href) return reject();
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = resolve;
    link.onerror = reject;
    document.head.appendChild(link);
  });

  const createImg = (src, className) => {
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.loading = 'lazy';
    img.draggable = false;
    img.className = className;
    return img;
  };

  class AbRecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      // Back/forward navigation can restore this page from the browser's back-forward cache (iOS Safari does this
      // on "back" from a product page) without re-running scripts: re-read the saved products when that happens.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.update();
      });

      this.update();
    }

    update() {
      const context = this.dataset.context;
      const items = readSavedProducts()
        // Out-of-stock products are left out (filtered before the limit, so up to 8 in-stock products still show).
        .filter((item) => item && item.ctx === context && item.url && item.title && item.available && item.variantId)
        .slice(0, MAX_VISIBLE);

      if (items.length === 0) {
        this.hidden = true;
        return;
      }

      // Stays hidden if the stylesheet fails to load: never show an unstyled panel.
      this.stylesheetLoaded = this.stylesheetLoaded || loadStylesheet(this.dataset.stylesheet);
      this.stylesheetLoaded.then(() => {
        if (!this.ready) this.init();
        this.items = items;
        this.renderThumbs();
        this.renderCards();
        this.hidden = false;
      }, () => {});
    }

    // One-time setup (listeners), run the first time there is something to show.
    init() {
      this.ready = true;
      this.toggleButton = this.querySelector('.ab-rv__toggle');
      this.content = this.querySelector('.ab-rv__content');

      this.toggleButton.addEventListener('click', () => this.setOpen(!this.isOpen));
      this.addEventListener('click', this.onClick.bind(this));
      // Direct add failed (stale snapshot, sold out, ...): fall back to the options pop-up.
      this.addEventListener('cart:error', this.onCartError.bind(this));
      document.addEventListener('keydown', this.onKeydown.bind(this));
      // Collapse after a product was added from the panel (direct add, or via our pop-up, which lives in <body> while open).
      document.addEventListener('variant:add', (event) => {
        if (this.contains(event.target) || this.quickBuyModalElement?.contains(event.target)) this.setOpen(false);
      });

      // Mobile: the bar sticks right below the (sticky) header group.
      this.updateStickyTop = this.updateStickyTop.bind(this);
      this.onScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateStickyTop();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', () => {
        this.collectStickySections();
        this.updateStickyTop();
      });

      // Tracks whether the bar is actually displayed (0 while gated by Intelligems / display: none). Only then:
      // - html.ab-rv-bar-visible + --ab-rv-bar-height push the theme's sticky collection toolbar below our bar (mobile)
      // - the sticky offset is computed on scroll
      // So the control group's page is untouched.
      new ResizeObserver(([entry]) => {
        const height = entry.target.offsetHeight;
        this.barVisible = height > 0;
        document.documentElement.classList.toggle('ab-rv-bar-visible', this.barVisible);
        document.documentElement.style.setProperty('--ab-rv-bar-height', `${this.barVisible ? height + 1 : 0}px`); // + panel bottom border
        if (this.barVisible) {
          this.collectStickySections();
          this.updateStickyTop();
        }
      }).observe(this.toggleButton);
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    setOpen(open) {
      this.toggleAttribute('open', open);
      this.toggleButton.setAttribute('aria-expanded', String(open));
      this.content.hidden = !open;
    }

    renderThumbs() {
      const thumbs = this.querySelector('.ab-rv__thumbs');
      thumbs.replaceChildren();
      this.items
        .filter((item) => item.image)
        .slice(0, MAX_THUMBS)
        .forEach((item) => {
          const thumb = document.createElement('span');
          thumb.className = 'ab-rv__thumb';
          thumb.appendChild(createImg(item.image, 'ab-rv__thumb-image'));
          thumbs.appendChild(thumb);
        });
    }

    renderCards() {
      const list = this.querySelector('.ab-rv__list');
      list.replaceChildren();
      const cardTemplate = this.querySelector('template[data-ab-rv-card]');
      const addTemplate = this.querySelector('template[data-ab-rv-add]');
      const optionsTemplate = this.querySelector('template[data-ab-rv-options]');
      const imageClass = this.dataset.imageClass || 'product-card__image product-card__image--primary';

      this.items.forEach((item, index) => {
        const card = cardTemplate.content.firstElementChild.cloneNode(true);
        card.dataset.index = String(index);

        const media = card.querySelector('.ab-rv-card__media');
        media.href = item.url;
        media.setAttribute('aria-label', item.title);
        if (item.image) media.appendChild(createImg(item.image, imageClass));
        if (item.image2) media.appendChild(createImg(item.image2, 'product-card__image product-card__image--secondary ab-rv-card__image--secondary'));

        const title = card.querySelector('.ab-rv-card__title');
        title.href = item.url;
        title.textContent = item.title;

        const price = card.querySelector('.ab-rv-card__price');
        price.textContent = item.price || '';
        if (item.compareAtPrice) {
          price.classList.add('text-on-sale');
          const compareAt = card.querySelector('.ab-rv-card__compare');
          compareAt.textContent = item.compareAtPrice;
          compareAt.hidden = false;
        } else {
          price.classList.add('text-subdued');
        }

        const figure = card.querySelector('.product-card__figure');

        if (item.needsOptions) {
          figure.appendChild(optionsTemplate.content.firstElementChild.cloneNode(true));
        } else {
          const productForm = addTemplate.content.firstElementChild.cloneNode(true);
          // Must be set before the <product-form> is connected (it reads form.id on connect).
          productForm.querySelector('input[name="id"]').value = item.variantId;
          figure.appendChild(productForm);
        }

        list.appendChild(card);
      });
    }

    itemFor(element) {
      const card = element.closest('.ab-rv-card');
      return card ? this.items[Number(card.dataset.index)] : null;
    }

    onClick(event) {
      if (event.target.closest('[data-ab-rv-close]')) {
        this.setOpen(false);
        return;
      }

      const optionsButton = event.target.closest('[data-ab-rv-options]');
      if (optionsButton) {
        this.openQuickBuy(this.itemFor(optionsButton), optionsButton);
      }
    }

    onCartError(event) {
      event.stopPropagation();
      const item = this.itemFor(event.target);
      if (item) this.openQuickBuy(item, event.target.querySelector('button[type="submit"]'));
    }

    onKeydown(event) {
      // Escape already handled by an open theme modal/drawer (focus-trap calls preventDefault) must not close us too.
      if (event.key !== 'Escape' || event.defaultPrevented || !this.isOpen) return;
      this.setOpen(false);
      this.toggleButton.focus();
    }

    // One shared instance of the theme's quick-buy pop-up, created on first use (not present on the page before).
    get quickBuyModal() {
      if (!this.quickBuyModalElement) {
        this.quickBuyModalElement = document.createElement('quick-buy-modal');
        this.quickBuyModalElement.className = 'quick-buy-modal modal';
        this.quickBuyModalElement.id = 'ab-rv-quick-buy';
        this.appendChild(this.quickBuyModalElement);
      }
      return this.quickBuyModalElement;
    }

    async openQuickBuy(item, trigger) {
      if (!item) return;
      const modal = this.quickBuyModal;

      if (typeof modal.show !== 'function') {
        window.location.href = item.url;
        return;
      }
      if (modal.open) return;

      modal.setAttribute('handle', item.variantId ? `${item.handle}?variant=${item.variantId}` : item.handle);
      trigger?.setAttribute('aria-busy', 'true');

      try {
        await modal.show();
      } catch (error) {
        // Product page could not be turned into a pop-up (e.g. template without quick-buy content): go to the PDP.
        window.location.href = item.url;
      } finally {
        trigger?.removeAttribute('aria-busy');
      }
    }

    collectStickySections() {
      this.stickySections = Array.from(document.querySelectorAll('.shopify-section-group-header-group')).filter((section) => {
        const position = getComputedStyle(section).position;
        return position === 'sticky' || position === 'fixed';
      });
    }

    updateStickyTop() {
      if (DESKTOP_QUERY.matches || !this.barVisible) return;

      // Computed from the real position so it keeps working if the header hides/reappears on scroll.
      const top = this.stickySections.reduce((max, section) => {
        const rect = section.getBoundingClientRect();
        return rect.height > 0 ? Math.max(max, rect.bottom) : max;
      }, 0);

      this.style.setProperty('--ab-rv-top', `${Math.max(0, Math.round(top))}px`);
    }
  }

  window.customElements.define('ab-recently-viewed', AbRecentlyViewed);
})();
